import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { InputBase, type InputBaseProps } from "@core/ui";
import { ThemeType } from "@core/types/style.types";

export type SizeType = "default" | "hero";

export type Props = InputBaseProps & {
  /** Transform value on change (e.g., uppercase) */
  transformOnChange?: (val: string) => string;
  /** Whether field has a prefix element (affects border radius) */
  prefix?: boolean;
  /** Whether field has an icon (affects padding) */
  isWithIcon?: boolean;
  /** Theme variant for light/dark backgrounds */
  theme?: ThemeType;
  /** Size variant for different contexts */
  size?: SizeType;
  /** Success state - shows success border color */
  success?: boolean;
};

/**
 * Styled input component.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Standalone usage (without RHF)
 * const [email, setEmail] = useState("");
 * <Input
 *   name="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   error={!!errors.email}
 *   placeholder="Enter email"
 * />
 *
 * @example
 * // With FormField (includes label and error display)
 * import { FormField } from "@themes/pollinator/ui/FormField";
 * import { Form } from "@themes/pollinator/ui/Form";
 *
 * <Form form={form}>
 *   <FormField name="email" label="Email" component="Input" required />
 * </Form>
 */
export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      className,
      type = "text",
      error,
      disabled,
      prefix,
      isWithIcon,
      onChange,
      onBlur,
      transformOnChange,
      theme = "light",
      size = "default",
      success,
      required: _required, // Filtered to prevent HTML5 validation bubbles
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (transformOnChange && e?.target?.value != null) {
        e.target.value = transformOnChange(e.target.value);
      }
      onChange?.(e);
    };

    return (
      <InputBase
        ref={ref}
        name={name}
        type={type}
        error={error}
        disabled={disabled}
        onChange={handleChange}
        onBlur={onBlur}
        className={twMerge(
          // Base styles
          "border rounded-sm shadow-sm transition-colors w-full",
          // Size styles
          size === "default" && "text-sm px-3 py-2",
          size === "hero" && "text-lg px-4 py-3",
          // Light theme styles (default)
          theme === "light" && [
            "bg-transparent",
            "placeholder:text-brand-400",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
            "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300 disabled:border-brand-300",
          ],
          // Dark theme styles
          theme === "dark" && [
            "bg-brand-50 border-brand-600 text-brand-800",
            "placeholder:text-brand-400",
            "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400",
            "disabled:cursor-not-allowed disabled:bg-brand-800 disabled:text-brand-500 disabled:border-brand-700",
          ],
          // Error state
          error && "border-error-600 border-2",
          // Success state
          success && "border-success border-2 disabled:border-success disabled:bg-brand-50 disabled:text-success",
          // Prefix modifier (removes left border radius)
          prefix && "rounded-l-none",
          // Icon modifier (adds left padding)
          isWithIcon && "pl-10",
          className
        )}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

export default Input;

